import { Component, Input, OnInit } from '@angular/core';

import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { finalize } from 'rxjs/operators';
import { PopupService } from '../../../../../../shared/services/popup/popup.service';
import { ErrorService } from '../../../../../../core/services/error.service';
import { BlockUiService } from '../../../../../../shared/services/blockUi/block-ui.service';
import { MarketAnnouncementService } from '../../../../../../core/services/api/mysec/market-announcement.service';
import { AnnouncementModel } from '../../../../../../core/models/campaign/announcements.model';
import { AnnouncementAudience } from '../../../../../../core/enum/mysec/announcement-category.enum';

@Component({
  selector: 'app-post-new-announcement',
  templateUrl: './post-new-announcement.component.html',
  styleUrls: ['./post-new-announcement.component.scss'],
  imports: [ReactiveFormsModule, TranslateModule, MatSelectModule],
})
export class PostNewAnnouncementComponent implements OnInit {
  @Input() isViewOnly: boolean = false;
  @Input() companyId?: number;
  @Input() announcement: AnnouncementModel | null = null;

  form!: UntypedFormGroup;
  showFormError: boolean = false;

  AnnouncementAudience: typeof AnnouncementAudience = AnnouncementAudience;
  audienceOptions = [
    { id: AnnouncementAudience.All, labelKey: 'common.all' },
    { id: AnnouncementAudience.InvestorOnly, labelKey: 'common.investorOnly' },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private popupService: PopupService,
    private blockUiService: BlockUiService,
    private marketService: MarketAnnouncementService,
    private errorService: ErrorService,
  ) {}

  ngOnInit() {
    if (!this.form) {
      this.buildForm();
    }
  }

  buildForm() {
    this.form = this.formBuilder.group({
      title: ['', [Validators.required]],
      body: ['', [Validators.required]],
      audience: [null, [Validators.required]],
    });
    if (this.announcement) {
      this.form.patchValue({
        title: this.announcement.title,
        body: this.announcement.body,
        // Announcements posted before audience was issuer-selectable have no
        // value stored; they were all public, so they read back as All.
        audience: this.announcement.audience ?? AnnouncementAudience.All,
      });
    }
    if (this.isViewOnly) {
      this.form.get('audience').disable();
    }
  }

  confirmSubmit() {
    this.popupService
      .confirm('raise.confirmPostingAnnouncement', {
        titleLabel: 'raise.confirm',
        okLabel: 'raise.post',
        cancelLabel: 'common.cancel',
      })
      .then(
        () => {
          this.submit(false);
        },
        (response) => {
          if (response) {
            this.submit(false);
          }
        },
      );
  }

  submit(isPublished: boolean) {
    if (this.form.invalid) {
      this.showFormError = true;
      return;
    }

    if (this.companyId == null) {
      this.popupService.alert('Company context is missing. Please try again later.');
      return;
    }

    let announcement: AnnouncementModel = this.form.value;

    this.blockUiService.open();
    announcement.category = 1;
    announcement.isPublished = isPublished;
    let observable = this.marketService.createMarketAnnouncement(this.companyId, announcement);

    let alertMessage = 'raise.announcementAdded';

    observable.pipe(finalize(() => this.blockUiService.close())).subscribe({
      next: () => {
        this.popupService.success(alertMessage).then(() => {
          this.announcement = null;
          this.activeModal.close({ success: true });
          //this.getMarketAnnouncement(true);
        });
      },
      error: (err) => this.errorService.showError(err),
    });
  }

  cancel() {
    this.activeModal.dismiss();
  }
}
